// Read every Shopify result page before applying the storefront page size.
export const sequenceValue = (value) => {
  const text = value?.trim();
  return text !== '' && text !== undefined && Number.isFinite(Number(text))
    ? Number(text) : Infinity;
};

export const orderProducts = (products) => products
  .map((product, index) => ({ product, index, sequence: sequenceValue(product.dataset.customProductSequence) }))
  .sort((a, b) => a.sequence - b.sequence || a.index - b.index)
  .map(({ product }) => product);

class SequencedCollection extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.grid = this.querySelector('[data-custom-product-sequence-enabled]');
    this.page = Number(this.dataset.page);
    this.pageSize = Number(this.dataset.pageSize);
    this.pager = document.createElement('div');
    this.append(this.pager);
    this.pager.addEventListener('click', (event) => {
      if (!this.products) return;
      const link = event.target.closest('a[href]');
      if (!link || !new URL(link.href).searchParams.has('page')) return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const page = Number(new URL(link.href).searchParams.get('page'));
      this.renderPage(page, this.dataset.paginationType !== 'links');
      if (this.dataset.paginationType === 'links') {
        history.pushState({ FacetUrl: link.href, section_id: this.dataset.sectionId }, '', link.href);
        this.grid.scrollIntoView({ block: 'start' });
      }
    }, { signal: this.controller.signal });
    if (this.dataset.paginationType === 'links') {
      // Let the existing facet controller restore pages and filters on Back/Forward.
      history.replaceState({ ...history.state, FacetUrl: window.location.href, section_id: this.dataset.sectionId }, '', window.location.href);
    }
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  async load() {
    const signal = this.controller.signal;
    const timeout = setTimeout(() => {
      this.controller.abort(new DOMException('Collection loading timed out', 'TimeoutError'));
    }, 30000);
    this.setAttribute('aria-busy', 'true');
    this.grid.style.visibility = 'hidden';
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent = 'Loading products…';
    this.prepend(status);
    try {
      const pages = new Array(Number(this.dataset.pages));
      pages[this.page - 1] = this;
      const pending = Array.from({ length: pages.length }, (_, i) => i + 1).filter((p) => p !== this.page);
      // Limit concurrent section requests, and preserve source page order for ties.
      await Promise.all(Array.from({ length: Math.min(3, pending.length) }, async () => {
        while (pending.length && !signal.aborted) {
          const page = pending.shift();
          const url = new URL(this.dataset.pageUrl, window.location.origin);
          url.searchParams.set('page', page);
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal });
          if (!response.ok) throw new Error(`Collection page ${page}: ${response.status}`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = doc.querySelector('hdt-sequenced-collection');
          if (!section || section.dataset.total !== this.dataset.total || section.dataset.page !== String(page)) {
            throw new Error('Collection changed while loading');
          }
          pages[page - 1] = section;
        }
      }));
      if (signal.aborted) throw signal.reason;
      const products = [];
      this.banners = [];
      this.pagination = [];
      pages.forEach((section, pageIndex) => {
        let position = pageIndex * this.pageSize;
        const grid = section.querySelector('[data-custom-product-sequence-enabled]');
        for (const child of grid.children) {
          if (child.matches('hdt-card-product')) {
            products.push(child);
            position++;
          } else if (child.matches('.hdt-collection-promo-banner')) {
            this.banners.push({ element: child, position });
          }
        }
        this.pagination.push(section.querySelector('template[data-sequence-pagination]').content.cloneNode(true));
      });
      if (products.length !== Number(this.dataset.total)) throw new Error('Incomplete collection results');
      this.products = orderProducts(products);
      this.renderPage(this.page, false);
      status.remove();
    } catch (error) {
      if (signal.aborted && signal.reason?.name !== 'TimeoutError') return;
      console.error('Custom collection sequence:', error);
      // Keep the complete native navigation available if a section request fails.
      this.pager.replaceChildren(this.querySelector('template[data-sequence-pagination]').content.cloneNode(true));
      status.textContent = 'Product ordering could not be loaded. Reload the page to retry.';
    } finally {
      clearTimeout(timeout);
      if (!signal.aborted || signal.reason?.name === 'TimeoutError') {
        this.grid.style.visibility = '';
        this.removeAttribute('aria-busy');
      }
    }
  }

  renderPage(page, append) {
    if (!this.products || page < 1 || page > this.pagination.length) return;
    this.observer?.disconnect();
    const start = append ? this.page * this.pageSize : (page - 1) * this.pageSize;
    const end = Math.min(page * this.pageSize, this.products.length);
    const children = [];
    for (let index = start; index < end; index++) {
      children.push(this.products[index]);
      this.banners.filter((banner) => banner.position === index + 1)
        .forEach((banner) => children.push(banner.element));
    }
    if (append) this.grid.append(...children);
    else this.grid.replaceChildren(...children);
    this.page = page;
    const pagination = this.pagination[page - 1].cloneNode(true);
    // These controls now paginate the globally sorted pool, not Shopify's raw order.
    pagination.querySelectorAll('wrapp-hdt-lm-main-url, wrapp-hdt-facet-url').forEach((wrapper) => {
      const replacement = document.createElement('div');
      replacement.append(...wrapper.childNodes);
      wrapper.replaceWith(replacement);
    });
    this.pager.replaceChildren(pagination);
    this.grid.dispatchEvent(new CustomEvent('products:update', { bubbles: true }));
    if (this.grid.reveal) {
      this.grid.prependEl = '';
      this.grid.appendEl = '';
      this.grid.reveal();
    }
    if (this.dataset.paginationType === 'infinite') {
      const next = this.pager.querySelector('a[href*="page="]');
      if (next) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.renderPage(this.page + 1, true);
        });
        this.observer.observe(next);
      }
    }
  }
}

customElements.define('hdt-sequenced-collection', SequencedCollection);
